/**
 * What a computed cell was calculated FROM (contract v1.3, units + property in
 * v1.4).
 *
 * Replaces the bare "calculated" line the panel used to show. A derived number
 * with no visible inputs asks the user to trust arithmetic they cannot inspect,
 * which is the opposite of the promise this dashboard makes — so each input is
 * named, valued and cited, and the ones a human replaced are marked, because an
 * overridden input is exactly why a total can stop matching its documents.
 *
 * LAYOUT (DOP-294): a label and its value are ONE reading, so they sit next to
 * each other. They used to be pushed to opposite ends of the row
 * (`justify-between`), which is fine in the narrow details panel and unreadable
 * in the in-grid expansion — that tile spans every period column, so a label on
 * the far left and its number on the far right could be a foot of whitespace
 * apart and the eye had to guess the pairing. Now every part of one input —
 * label, value, citation, Override — is adjacent in a single wrapping row, and
 * the list itself is width-capped so it stays readable no matter how wide the
 * grid behind it grows.
 *
 * Contributors are GROUPED under their property rather than repeating it per
 * row. A rollup lists the same metric once per property, and a multi-property
 * breakdown that repeats the name on every line reads as a ragged list instead
 * of as a structure.
 *
 * EACH INPUT IS FORMATTED IN ITS OWN UNIT. The inputs of a computed cell are
 * routinely measured in something else — a vacancy rate (`pct`) is built from
 * unit `count`s — and formatting them with the cell's unit turned 100 units
 * into "10,000.00%": wrong by a unit and two orders of magnitude, while looking
 * entirely plausible. An input whose unit the server did not send is rendered as
 * a bare number rather than dressed in the cell's.
 */

import { AlertTriangle, ExternalLink, Pencil } from "lucide-react";
import React, { useState } from "react";

import type {
	KpiOverrideOutcome,
	KpiRevertOutcome,
} from "../../services/kpiService";
import type { KpiCellInput } from "../../types/kpi";
import { formatKpiValueExact, formatUnitlessValue } from "./formatKpiValue";
import { kpiInputCitedNumber } from "./kpiCellAction";
import { kpiSourceKind } from "./kpiSourceUri";
import KpiSourceLink from "./KpiSourceLink";
import KpiOverrideControl from "./KpiOverrideControl";
import { useKpiSourceOpener } from "./useKpiSourceOpener";

export interface KpiInputListProps {
	inputs: KpiCellInput[];
	/**
	 * Correcting a component in place. Optional only so a read-only surface can
	 * omit it; where it is given, every component backed by a stored row gets
	 * the same override a raw cell has — otherwise the panel's advice to
	 * "override the value you mean" points at nothing, and for split components
	 * (a payroll's two halves) there is no grid row to point at either.
	 */
	onOverride?: (
		valueId: string,
		value: number,
	) => Promise<KpiOverrideOutcome>;
	/**
	 * Undo an override on a component in place, restoring the extracted value.
	 * Given wherever `onOverride` is: an overridden component shows the same
	 * revert its own edit control does, so the breakdown can undo what the
	 * panel offered.
	 */
	onRevert?: (valueId: string) => Promise<KpiRevertOutcome>;
}

function sourceLine(input: KpiCellInput): string | null {
	const source = input.source;
	if (!source) return null;
	const parts = [source.file];
	if (source.sheet) parts.push(source.sheet);
	if (source.ref) parts.push(source.ref);
	if (source.page) parts.push(`p.${source.page}`);
	return parts.join(" · ");
}

/**
 * A key that survives a rollup. Under a portfolio cell the same `metric_key`
 * and `role` appear once per property, so those two alone collide and React
 * reconciles rows onto each other; the property and the cited location are what
 * actually tell two contributor rows apart.
 */
function inputKey(input: KpiCellInput, index: number): string {
	const source = input.source;
	const cited = source
		? `${source.file}#${source.sheet ?? ""}!${source.ref ?? ""}:${source.page ?? ""}`
		: "";
	return [
		input.property_key ?? "",
		input.metric_key,
		input.role ?? "",
		cited,
		index,
	].join("|");
}

/** One RUN of consecutive inputs that share a property. */
export interface KpiInputGroup {
	/**
	 * React key for this group. Carries the run's position AND a prefix naming
	 * the kind, so a property literally called "none" cannot collide with the
	 * unlabelled group, and two runs of the same property stay distinct.
	 */
	id: string;
	propertyKey: string | null;
	inputs: KpiCellInput[];
}

/**
 * Consecutive inputs sharing a property, gathered under one heading.
 *
 * RUNS, not buckets, and the distinction is the whole correctness argument. The
 * server's order is meaningful — it is the order the arithmetic consumed the
 * operands in — so gathering all of property A's inputs into one bucket would
 * render A1, A2, B1 for a payload of A1, B1, A2 and show a breakdown that does
 * not match the calculation it explains. Grouping only what is already adjacent
 * cannot reorder anything: the output is the input, partitioned.
 *
 * A rollup lists its contributors property by property, so in practice this is
 * one heading per property. A payload that interleaves gets a heading per run —
 * repetitive, and still in the right order, which is the correct trade.
 *
 * Inputs with no property — the ordinary derived case — form unlabelled runs,
 * which is why a single-property breakdown renders as a plain flat list.
 */
export function groupInputsByProperty(
	inputs: KpiCellInput[],
): KpiInputGroup[] {
	const groups: KpiInputGroup[] = [];
	inputs.forEach((input) => {
		const propertyKey = input.property_key ?? null;
		const last = groups[groups.length - 1];
		if (last && last.propertyKey === propertyKey) {
			last.inputs.push(input);
			return;
		}
		groups.push({
			id: `${groups.length}:${
				propertyKey === null ? "none" : `property:${propertyKey}`
			}`,
			propertyKey,
			inputs: [input],
		});
	});
	return groups;
}

function formatInputValue(input: KpiCellInput): string {
	return input.unit
		? formatKpiValueExact(input.value ?? null, input.unit)
		: formatUnitlessValue(input.value ?? null);
}

const KpiInputList: React.FC<KpiInputListProps> = ({
	inputs,
	onOverride,
	onRevert,
}) => {
	const openSource = useKpiSourceOpener();
	// Which rows have their editor open. Closed by default: the list is read
	// first and corrected rarely, and a column of input boxes reads as a form.
	const [editing, setEditing] = useState<Set<string>>(new Set());

	const groups = groupInputsByProperty(inputs);
	// The global index across groups, so a key stays unique after grouping.
	let position = -1;

	return (
		// Width-capped, and that cap is the fix: this list renders inside a tile
		// that spans the whole grid, and a row allowed to use that width puts a
		// label and its number a foot apart.
		<div className="mt-2 max-w-2xl space-y-3">
			{groups.map((group) => (
				<div key={group.id}>
					{group.propertyKey && (
						// Named once, above its rows, instead of repeated on each of
						// them — that repetition is what made a multi-property
						// breakdown read as a ragged list.
						<p className="text-xs font-medium text-text-strong">
							{group.propertyKey}
						</p>
					)}
					<ul className={group.propertyKey ? "mt-1 space-y-1.5" : "space-y-1.5"}>
						{group.inputs.map((input) => {
							position += 1;
							const citation = sourceLine(input);
							const key = inputKey(input, position);
							// Only a component with a stored row behind it can be
							// corrected; one that is itself computed has no row to
							// write to.
							const editableId = onOverride ? input.value_id ?? null : null;
							const isEditing = editableId !== null && editing.has(key);
							const openable = input.source
								? kpiSourceKind(input.source) !== "unopenable"
								: false;
							return (
								<li key={key} className="border-l-2 border-gray-200 pl-2">
									{/* One wrapping row: label, value, then everything
									    that qualifies them. Nothing is pushed to an
									    opposite edge, so the pairing survives any
									    container width. */}
									<div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
										<span className="text-base text-text-base">
											{input.metric_key}
											{input.role && (
												<span className="ml-1 text-xs text-text-muted">
													({input.role})
												</span>
											)}
										</span>
										<span className="font-medium tabular-nums text-base text-text-base">
											{formatInputValue(input)}
										</span>
										{input.overridden && (
											<span className="inline-flex items-center gap-1 rounded bg-[#E6F2F1] px-1.5 py-0.5 text-xs font-medium text-[#004C4B]">
												<AlertTriangle
													className="h-3 w-3"
													aria-hidden="true"
												/>
												overridden
											</span>
										)}
										{input.confidence === "unverified" && (
											<span className="rounded bg-amber-50 px-1.5 py-0.5 text-xs font-medium text-amber-700">
												unverified
											</span>
										)}
										{input.source ? (
											openable ? (
												// The citation IS the control: clicking it
												// opens this component's document in the
												// viewer popup, at its cited cell.
												<button
													type="button"
													onClick={() =>
														openSource(input.source, kpiInputCitedNumber(input))
													}
													className="inline-flex min-w-0 items-center gap-1 rounded text-left text-xs text-[#008782] underline decoration-dotted underline-offset-2 hover:text-[#004C4B]"
												>
													<span className="break-words">{citation}</span>
													<ExternalLink
														className="h-3 w-3 shrink-0 text-text-muted"
														aria-hidden="true"
													/>
												</button>
											) : (
												// Not renderable here (no attachment id, or a
												// type the viewers do not handle) — the
												// citation still opens the file.
												<KpiSourceLink
													source={input.source}
													cited={kpiInputCitedNumber(input)}
													label={citation ?? input.source.file}
													className="min-w-0 break-words text-xs text-[#008782] underline decoration-dotted underline-offset-2 hover:text-[#004C4B]"
												/>
											)
										) : (
											<span className="text-xs text-text-muted">
												no source — itself calculated
											</span>
										)}
										{editableId !== null && (
											<button
												type="button"
												onClick={() =>
													setEditing((current) => {
														const next = new Set(current);
														if (next.has(key)) next.delete(key);
														else next.add(key);
														return next;
													})
												}
												aria-expanded={isEditing}
												className="inline-flex items-center gap-1 rounded px-1 text-xs font-medium text-text-muted hover:bg-gray-100 hover:text-text-base"
											>
												<Pencil className="h-3 w-3" aria-hidden="true" />
												{isEditing ? "Cancel" : "Override"}
											</button>
										)}
									</div>
									{isEditing && editableId !== null && onOverride && (
										<KpiOverrideControl
											valueId={editableId}
											unit={input.unit ?? "usd"}
											size="compact"
											extractedValue={input.extracted_value ?? null}
											isOverridden={input.overridden === true}
											onRevert={onRevert}
											label={
												<>
													Correcting{" "}
													<span className="font-medium text-text-base">
														{input.property_key
															? `${input.property_key} · ${input.metric_key}`
															: input.metric_key}
													</span>
												</>
											}
											onOverride={onOverride}
										/>
									)}
								</li>
							);
						})}
					</ul>
				</div>
			))}
		</div>
	);
};

export default KpiInputList;
